import {
  readLastTouchCookie,
  readUtmFromQuery,
  type UtmKey,
  type UtmSnapshot,
  UTM_KEYS,
} from "@/lib/utm/qbUtmCookie";
import type { CheckoutPlacementKey } from "./placements";

export type CheckoutTracking = Readonly<
  Partial<
    Record<UtmKey | "qb_placement_key" | "qb_plan_id" | "qb_utm_id", string>
  >
>;

export type DirectCheckoutPlan = {
  readonly checkoutUrl?: string | null;
  readonly id: string;
  readonly slug?: string | null;
};

export const CANONICAL_CHECKOUT_URLS = {
  mensal: "https://go.pagfy.com/u2phd",
  semestral: "https://go.pagfy.com/tq31o",
  anual: "https://go.pagfy.com/dxiv5",
  "pro-addon": "https://go.pagfy.com/fijqi",
  "topup-100": "https://go.pagfy.com/cbj2i",
  "topup-unlimited": "https://go.pagfy.com/vlyvu",
  "entrada-2990": "https://go.pagfy.com/cdnfm",
} as const;

type CanonicalCheckoutPlanId = keyof typeof CANONICAL_CHECKOUT_URLS;

type CheckoutTarget = {
  readonly checkoutUrl: string;
  readonly planId: string;
};

const CANONICAL_CHECKOUT_ALIASES: Readonly<
  Record<string, CanonicalCheckoutPlanId>
> = {
  "plan-anual": "anual",
  "plan-mensal": "mensal",
  "plan-semestral": "semestral",
  "recarga-ilimitada": "topup-unlimited",
  "recarga-ilimitado": "topup-unlimited",
  "tokens-100": "topup-100",
  "topup-ilimitada": "topup-unlimited",
  "topup-ilimitado": "topup-unlimited",
};

export function buildOwnedUtmId(
  planId: string,
  placementKey: CheckoutPlacementKey,
): string {
  return `qb-${normalizeTrackingValue(placementKey)}-${normalizeTrackingValue(planId)}`;
}

export function buildInternalCheckoutTracking(
  planId: string,
  placementKey: CheckoutPlacementKey,
): CheckoutTracking {
  const ownedUtmId = buildOwnedUtmId(planId, placementKey);
  return {
    // Legacy parity: every internal checkout claims utm_campaign with the
    // owned id (legacy app/comprar/_components/_lib/landing-utms.ts:25-26 via
    // appendOwnedMarketingUtmCampaign). Pagfy echoes it in the webhook
    // tracking, which is how the marketing CRM buckets the sale as INTERNA.
    utm_campaign: ownedUtmId,
    qb_placement_key: placementKey,
    qb_plan_id: planId,
    qb_utm_id: ownedUtmId,
  };
}

export function readCheckoutExternalTracking(): UtmSnapshot {
  if (typeof document === "undefined") return {};
  return {
    ...readLastTouchCookie(document.cookie),
    ...readCurrentQueryTracking(),
  };
}

export function buildTrackedCheckoutUrl(
  checkoutUrl: string,
  tracking: CheckoutTracking,
): string {
  return appendTrackingParams(checkoutUrl, {
    ...readCheckoutExternalTracking(),
    ...tracking,
  });
}

export function buildCheckoutPath(
  planId: string,
  placementKey: CheckoutPlacementKey,
): string {
  const params = new URLSearchParams({
    plan: planId,
    placementKey,
    ...buildInternalCheckoutTracking(planId, placementKey),
  });
  return `/checkout?${params.toString()}`;
}

export function directCheckoutHref(
  plan: DirectCheckoutPlan,
  placementKey: CheckoutPlacementKey,
): string | null {
  const target = checkoutTargetForPlan(plan);
  if (!target) return null;
  return checkoutHrefForTarget(target, placementKey);
}

export function canonicalCheckoutHref(
  planId: string,
  placementKey: CheckoutPlacementKey,
  externalTracking: CheckoutTracking = {},
): string | null {
  const target = canonicalCheckoutTarget(planId);
  if (!target) return null;
  return checkoutHrefForTarget(target, placementKey, externalTracking);
}

export function checkoutTrackingForCreate(
  planId: string,
  placementKey?: CheckoutPlacementKey,
): CheckoutTracking {
  return {
    ...readCheckoutExternalTracking(),
    ...(placementKey
      ? buildInternalCheckoutTracking(planId, placementKey)
      : {}),
  };
}

export function appendTrackingParams(
  rawUrl: string,
  tracking: CheckoutTracking,
): string {
  const isRelative = rawUrl.startsWith("/");
  const url = new URL(rawUrl, "https://quartelbot.com");
  for (const key of [
    ...UTM_KEYS,
    "qb_placement_key",
    "qb_plan_id",
    "qb_utm_id",
  ] as const) {
    const value = tracking[key];
    if (value && !url.searchParams.has(key)) url.searchParams.set(key, value);
  }
  // Owned placements OVERWRITE utm_campaign — legacy
  // appendOwnedMarketingUtmCampaign uses searchParams.set, so an internal
  // click always wins the campaign slot (marketing-utm-registry-helpers.ts:46).
  if (tracking.qb_utm_id && tracking.utm_campaign === tracking.qb_utm_id) {
    url.searchParams.set("utm_campaign", tracking.utm_campaign);
  }
  return isRelative
    ? `${url.pathname}${url.search}${url.hash}`
    : url.toString();
}

function normalizeTrackingValue(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");
}

function checkoutPlanSlug(plan: DirectCheckoutPlan): string {
  const slug = plan.slug?.trim();
  if (slug) return slug;
  return plan.id;
}

function checkoutTargetForPlan(
  plan: DirectCheckoutPlan,
): CheckoutTarget | null {
  const checkoutUrl = plan.checkoutUrl?.trim();
  if (checkoutUrl) return { checkoutUrl, planId: checkoutPlanSlug(plan) };
  return (
    canonicalCheckoutTarget(plan.id) ??
    canonicalCheckoutTarget(checkoutPlanSlug(plan))
  );
}

function checkoutHrefForTarget(
  target: CheckoutTarget,
  placementKey: CheckoutPlacementKey,
  externalTracking: CheckoutTracking = {},
): string {
  return appendTrackingParams(target.checkoutUrl, {
    ...readCheckoutExternalTracking(),
    ...externalTracking,
    ...buildInternalCheckoutTracking(target.planId, placementKey),
  });
}

function canonicalCheckoutTarget(planId: string): CheckoutTarget | null {
  const canonicalPlanId = canonicalCheckoutPlanId(planId);
  if (!canonicalPlanId) return null;
  return {
    checkoutUrl: CANONICAL_CHECKOUT_URLS[canonicalPlanId],
    planId: canonicalPlanId,
  };
}

function canonicalCheckoutPlanId(
  planId: string,
): CanonicalCheckoutPlanId | null {
  const normalized = normalizeCheckoutPlanKey(planId);
  if (isCanonicalCheckoutPlanId(normalized)) return normalized;
  return CANONICAL_CHECKOUT_ALIASES[normalized] ?? null;
}

function isCanonicalCheckoutPlanId(
  planId: string,
): planId is CanonicalCheckoutPlanId {
  return Object.hasOwn(CANONICAL_CHECKOUT_URLS, planId);
}

function normalizeCheckoutPlanKey(planId: string): string {
  return planId
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");
}

function readCurrentQueryTracking(): UtmSnapshot {
  if (typeof window === "undefined") return {};
  const search = window.location.search;
  return readUtmFromQuery(search);
}
